import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const NAVY = "#0A1628";
const GOLD = "#D4AF37";
const BG = "#F8F9FA";
const MUTED = "#5b5e69";

/**
 * Admin · Account Analytics (Item 20 · PM22)
 *
 * The six metrics the doc requires:
 *   1. call_forwarding_active — master activation
 *   2. calls answered per account (last 7 days)
 *   3. answer rate — answered / routed
 *   4. qualified job requests captured
 *   5. estimated dollar value captured (Dollars-Saved input)
 *   6. per-account COGS (telephony + STT + LLM + TTS)
 */
export default function AdminAnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await api.get(`/admin/analytics/accounts`, { params: { days } }).then((x) => x.data);
      setData(r);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [days]);

  if (loading && !data) return <div style={{ padding: 40, textAlign: "center" }}>Loading…</div>;

  const agg = data?.aggregate || {};
  return (
    <div style={{ background: BG, minHeight: "100vh", padding: "30px 20px" }} data-testid="admin-analytics-page">
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ color: GOLD, letterSpacing: 3, fontSize: 12, fontWeight: 700 }}>NEARNOW · ADMIN</div>
        <h1 style={{ fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 3, color: NAVY, fontSize: 44, margin: "4px 0 8px" }}>
          ACCOUNT ANALYTICS
        </h1>
        <p style={{ color: MUTED, marginBottom: 16 }}>
          The six-number weekly readout. If forwarding activation is under 50%, the problem is onboarding — not copy or ads.
        </p>

        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {[7, 14, 30].map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`window-${d}d`}
                    style={{ ...winBtn, background: days === d ? NAVY : "#fff", color: days === d ? "#fff" : NAVY }}>
              LAST {d} DAYS
            </button>
          ))}
        </div>

        {err && <div style={{ background: "#FDECEC", color: "#A32D2D", padding: "12px 16px", borderRadius: 10, marginBottom: 12 }}>{err}</div>}

        {/* 6-metric header */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 22 }}>
          <Kpi label="FORWARDING ACTIVE" value={`${agg.forwarding_active_accounts ?? 0} / ${agg.total_active_accounts ?? 0}`}
               sub={`${agg.forwarding_activation_pct ?? 0}% of active accounts`}
               warn={(agg.forwarding_activation_pct ?? 0) < 50}
               testid="kpi-forwarding" />
          <Kpi label="CALLS ANSWERED" value={agg.answered_total ?? 0}
               sub={`of ${agg.routed_total ?? 0} routed`} testid="kpi-answered" />
          <Kpi label="ANSWER RATE" value={`${agg.answer_rate_pct_total ?? 0}%`} testid="kpi-answer-rate" />
          <Kpi label="QUALIFIED CAPTURES" value={agg.qualified_total ?? 0} testid="kpi-qualified" />
          <Kpi label="WORK CAPTURED" value={`$${(agg.estimated_value_usd_total ?? 0).toLocaleString()}`}
               accent testid="kpi-value" />
          <Kpi label="COGS (7d)" value={`$${(agg.cogs_usd_total ?? 0).toLocaleString()}`} testid="kpi-cogs" />
        </div>

        <div style={{ background: NAVY, color: "#fff", borderRadius: 12, padding: 16, marginBottom: 20 }}>
          <div style={{ color: GOLD, fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 2, fontSize: 14 }}>
            FAIR-USE CEILING (RECOMMENDED)
          </div>
          <div style={{ marginTop: 6, fontSize: 15 }}>
            {data?.fair_use_ceiling_calls_per_month ?? 150} answered calls / account / month.
            Overage at $0.50 / call (env: <code style={{ color: GOLD }}>ARIA_FAIR_USE_MONTHLY_CALLS</code>). Even if you never enforce it, publish it.
          </div>
        </div>

        {/* PM28 · Reactivation Campaigns rollup */}
        {data?.reactivation && (
          <div data-testid="reactivation-rollup" style={{ marginTop: 22 }}>
            <h2 style={{ fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 2, color: NAVY, fontSize: 26, margin: "0 0 12px" }}>
              REACTIVATION CAMPAIGNS · FRONT OFFICE
            </h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 12 }}>
              <Kpi label="CAMPAIGNS" value={data.reactivation.totals.campaigns} testid="react-kpi-campaigns" />
              <Kpi label="SMS SENT" value={data.reactivation.totals.sent} testid="react-kpi-sent" />
              <Kpi label="REPLIES" value={data.reactivation.totals.replies}
                   sub={`${data.reactivation.totals.reply_rate_pct}% reply rate`} testid="react-kpi-replies" />
              <Kpi label="OPT-OUTS" value={data.reactivation.totals.opt_outs} testid="react-kpi-optouts" />
              <Kpi label="EST. $ RECOVERED"
                   value={`$${(data.reactivation.totals.estimated_recovered_usd || 0).toLocaleString()}`}
                   accent testid="react-kpi-recovered" />
            </div>
            {data.reactivation.accounts.length > 0 && (
              <div style={{ background: "#fff", border: "1px solid #e5e7ee", borderRadius: 12, overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }} data-testid="reactivation-rollup-table">
                  <thead>
                    <tr style={{ background: "#F1F3F6", textAlign: "left" }}>
                      <th style={th}>Business</th>
                      <th style={thR}>Campaigns</th>
                      <th style={thR}>Sent</th>
                      <th style={thR}>Replies</th>
                      <th style={thR}>Reply %</th>
                      <th style={thR}>Opt-outs</th>
                      <th style={thR}>Est. $ Recovered</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.reactivation.accounts.map((a) => (
                      <tr key={a.contractor_id} data-testid={`react-row-${a.contractor_id}`} style={{ borderTop: "1px solid #eef0f4" }}>
                        <td style={td}>{a.business_name || a.contractor_id}</td>
                        <td style={tdR}>{a.campaigns}</td>
                        <td style={tdR}>{a.sent}</td>
                        <td style={tdR}>{a.replies}</td>
                        <td style={tdR}>{a.reply_rate_pct}%</td>
                        <td style={tdR}>{a.opt_outs}</td>
                        <td style={tdR}>${(a.estimated_recovered_usd || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <h2 style={{ fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 2, color: NAVY, fontSize: 26, margin: "22px 0 12px" }}>
          PER-ACCOUNT ROLL-UP
        </h2>
        <div style={{ background: "#fff", border: "1px solid #e5e7ee", borderRadius: 12, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }} data-testid="analytics-table">
            <thead>
              <tr style={{ background: "#F1F3F6", textAlign: "left" }}>
                <th style={th}>Business</th>
                <th style={th}>Plan</th>
                <th style={th}>Fwd?</th>
                <th style={thR}>Routed</th>
                <th style={thR}>Answered</th>
                <th style={thR}>Answer %</th>
                <th style={thR}>Qualified</th>
                <th style={thR}>$ Captured</th>
                <th style={thR}>COGS</th>
              </tr>
            </thead>
            <tbody>
              {(data?.accounts || []).map((a) => (
                <tr key={a.id} data-testid={`row-${a.id}`} style={{ borderTop: "1px solid #eef0f4" }}>
                  <td style={td}>{a.business_name}</td>
                  <td style={td}>{(a.plan || "").toUpperCase()}</td>
                  <td style={td}>
                    {a.forwarding_active
                      ? <span style={{ color: "#1A7A4A", fontWeight: 700 }}>✓</span>
                      : <span style={{ color: "#A32D2D", fontWeight: 700 }}>✗</span>}
                  </td>
                  <td style={tdR}>{a.routed}</td>
                  <td style={tdR}>{a.answered}</td>
                  <td style={tdR}>{a.answer_rate_pct}%</td>
                  <td style={tdR}>{a.qualified}</td>
                  <td style={tdR}>${a.estimated_value_usd.toLocaleString()}</td>
                  <td style={tdR}>${a.cogs_usd.toLocaleString()}</td>
                </tr>
              ))}
              {(data?.accounts || []).length === 0 && (
                <tr><td colSpan={9} style={{ ...td, textAlign: "center", color: MUTED, padding: 30 }}>
                  No active accounts in the window.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const Kpi = ({ label, value, sub, accent, warn, testid }) => (
  <div data-testid={testid} style={{
    background: "#fff", border: `2px solid ${warn ? "#A32D2D" : accent ? GOLD : "#e5e7ee"}`,
    borderRadius: 12, padding: 16,
  }}>
    <div style={{ color: MUTED, fontSize: 11, letterSpacing: 2, fontWeight: 700 }}>{label}</div>
    <div style={{ color: warn ? "#A32D2D" : NAVY, fontFamily: "Bebas Neue, Arial, sans-serif", fontSize: 32, letterSpacing: 1, marginTop: 4 }}>{value}</div>
    {sub && <div style={{ color: MUTED, fontSize: 11, marginTop: 4 }}>{sub}</div>}
  </div>
);

const th = { padding: "10px 12px", fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 1.5, color: NAVY, fontSize: 13 };
const thR = { ...th, textAlign: "right" };
const td = { padding: "10px 12px", color: NAVY };
const tdR = { ...td, textAlign: "right" };
const winBtn = { border: `1.5px solid ${NAVY}`, padding: "8px 14px", borderRadius: 10, fontWeight: 700, letterSpacing: 1.5, fontSize: 12, fontFamily: "Bebas Neue, Arial, sans-serif", cursor: "pointer" };
